import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaHeart, FaImage, FaSpinner, FaUser } from "react-icons/fa";
import api from "../../../utils/api/axiosInstance";
import getImageUrl from "../../../utils/imageUtils";
import styles from "../../../styles/components/profile/MessagesPanel.module.css";

const postCache = new Map();
const pendingRequests = new Map();

const fetchPostPreview = async (postId) => {
  if (postCache.has(postId)) {
    return postCache.get(postId);
  }

  if (pendingRequests.has(postId)) {
    return pendingRequests.get(postId);
  }

  const promise = api
    .get(`/posts/${postId}`)
    .then((res) => {
      const post = res.data.post || res.data;
      const entry = { status: "ok", post };
      postCache.set(postId, entry);
      pendingRequests.delete(postId);
      return entry;
    })
    .catch((err) => {
      const entry = {
        status: "error",
        message:
          err.response?.data?.message || "This post is unavailable",
      };
      postCache.set(postId, entry);
      pendingRequests.delete(postId);
      return entry;
    });

  pendingRequests.set(postId, promise);
  return promise;
};

const getPostPreviewImage = (post) => {
  if (post?.productImages && typeof post.productImages === "object") {
    const productIds = Object.keys(post.productImages);
    for (const productId of productIds) {
      const images = post.productImages[productId];
      if (Array.isArray(images) && images[0]) {
        return images[0];
      }
    }
  }

  if (Array.isArray(post?.images) && post.images[0]) {
    return post.images[0];
  }

  return null;
};

const getPostPreviewText = (post) => {
  const caption = post?.caption?.trim();
  if (caption) return caption;

  const productNames =
    post?.products?.map((product) => product?.name).filter(Boolean) || [];
  if (productNames.length > 0) {
    return productNames.join(", ");
  }

  return "Shared post";
};

const SharedPostPreview = ({ postId, postPath }) => {
  const [preview, setPreview] = useState(() => postCache.get(postId) || null);

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      const result = await fetchPostPreview(postId);
      if (!cancelled) {
        setPreview(result);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [postId]);

  if (!preview || (preview.status === "ok" && !preview.post)) {
    return (
      <Link to={postPath} className={styles.sharedPostCard}>
        <div className={styles.sharedPostLoading}>
          <FaSpinner className={styles.spinner} />
          <span>Loading post...</span>
        </div>
      </Link>
    );
  }

  if (preview.status === "error") {
    return (
      <Link to={postPath} className={styles.sharedPostCard}>
        <div className={styles.sharedPostFallback}>
          <FaImage className={styles.sharedPostFallbackIcon} />
          <div>
            <span className={styles.sharedPostLabel}>Shared post</span>
            <span className={styles.sharedPostFallbackText}>
              {preview.message}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  const post = preview.post;
  const imagePath = getPostPreviewImage(post);
  const previewText = getPostPreviewText(post);
  const author = post.user;
  const likeCount = post.likeCount ?? post.likes?.length ?? 0;

  return (
    <Link to={postPath} className={styles.sharedPostCard}>
      <div className={styles.sharedPostMedia}>
        {imagePath ? (
          <img
            src={getImageUrl(imagePath)}
            alt=""
            className={styles.sharedPostImage}
          />
        ) : (
          <div className={styles.sharedPostImagePlaceholder}>
            <FaImage />
          </div>
        )}
        <span className={styles.sharedPostBadge}>Post</span>
      </div>

      <div className={styles.sharedPostBody}>
        {author && (
          <div className={styles.sharedPostAuthor}>
            {author.avatar ? (
              <img
                src={getImageUrl(author.avatar)}
                alt=""
                className={styles.sharedPostAuthorAvatar}
              />
            ) : (
              <span className={styles.sharedPostAuthorAvatarPlaceholder}>
                <FaUser />
              </span>
            )}
            <span className={styles.sharedPostAuthorName}>
              {author.firstName} {author.lastName}
            </span>
          </div>
        )}

        <p className={styles.sharedPostCaption}>{previewText}</p>

        {likeCount > 0 && (
          <div className={styles.sharedPostMeta}>
            <FaHeart className={styles.sharedPostMetaIcon} />
            <span>{likeCount}</span>
          </div>
        )}
      </div>
    </Link>
  );
};

export default SharedPostPreview;
